import { useEffect, useState } from 'react';
import Badge from '../Badge';
import LinkThumbnail from '../suppliers/LinkThumbnail';
import { formatJMD } from '../../lib/finances';
import {
  STOCK_TONE,
  needsRestock,
  productImageUrl,
  stockLabel,
  stockState,
  unitMargin,
} from '../../lib/products';
import { prettyUrl, toHttpUrl } from '../../lib/url';

// One product in the catalogue.
//
// The row is built around the stock control, because that's the thing touched
// most often: counting the shelf should be clicking a button, not opening an
// edit form. Everything else on the row is read-only until you ask to edit.
export default function ProductRow({
  product,
  preview,
  onAdjustStock,
  onSetStock,
  onEdit,
  onArchive,
  onDelete,
  onAddToOrder,
  reorderStatus,
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(String(product.stock_qty ?? 0));
  const [editingQty, setEditingQty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The count can be changed by the +/- buttons, by another person's browser,
  // or by a refetch after this row typed one in. Follow the saved value
  // whenever it changes underneath — except while it's actually being typed,
  // which would fight the keystrokes.
  useEffect(() => {
    if (!editingQty) setDraft(String(product.stock_qty ?? 0));
  }, [product.stock_qty, editingQty]);

  const state = stockState(product);
  const href = toHttpUrl(product.product_url);
  const margin = unitMargin(product);
  const photo = productImageUrl(product.image_path);

  const commitQty = () => {
    setEditingQty(false);
    const next = Math.max(0, Math.round(Number(draft) || 0));
    if (next !== Number(product.stock_qty ?? 0)) onSetStock(product, next);
    else setDraft(String(product.stock_qty ?? 0));
  };

  return (
    <li className={`product-row product-row-${state}`}>
      {/* An uploaded photo wins over the link preview: one is the picture you
          chose, the other is whatever the linked page happened to advertise.
          With neither, LinkThumbnail's own fallback handles it. */}
      {photo ? (
        <a
          className="toorder-thumb product-row-photo"
          href={href || photo}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <img src={photo} alt={product.name} loading="lazy" draggable={false} />
        </a>
      ) : (
        <LinkThumbnail href={href} preview={preview} alt={product.name} />
      )}

      <div className="product-row-main">
        <div className="product-row-title">
          <span className="product-row-name">{product.name}</span>
          {product.category && <Badge tone="neutral">{product.category}</Badge>}
          {!product.is_active && <Badge tone="muted">Archived</Badge>}
        </div>

        <div className="product-row-meta">
          {product.vendor?.name && <span>{product.vendor.name}</span>}
          {href && (
            <a
              className="product-row-link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={product.product_url}
            >
              {prettyUrl(product.product_url)}
            </a>
          )}
          {product.product_info && (
            <button
              type="button"
              className="link-button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? 'Hide info' : 'Product info'}
            </button>
          )}
        </div>

        {expanded && product.product_info && (
          <p className="product-row-info">{product.product_info}</p>
        )}
      </div>

      {/* Plain sans, like every other large figure in the app — the serif is
          for headings only. An unpriced product shows a dash, not J$0.00:
          zero reads as free rather than as unknown. */}
      <div className="product-row-price">
        {product.price_jmd == null ? (
          <span className="product-row-unpriced" title="No price set">
            —
          </span>
        ) : (
          formatJMD(product.price_jmd)
        )}
        {/* Margin sits under the price as a quiet second line rather than
            getting its own column: it's worth knowing per product, but this
            page is about stock, not profitability. */}
        {margin && (
          <span
            className={'product-row-margin' + (margin.profit < 0 ? ' negative' : '')}
            title={`Costs ${formatJMD(product.cost_jmd)}, sells for ${formatJMD(
              product.price_jmd
            )}`}
          >
            {margin.profit < 0 ? '−' : '+'}
            {formatJMD(Math.abs(margin.profit))}
            {margin.percent !== null && ` · ${margin.percent.toFixed(0)}%`}
          </span>
        )}
      </div>

      <div className="product-row-stock">
        <div className="product-stepper">
          <button
            type="button"
            onClick={() => onAdjustStock(product, -1)}
            disabled={Number(product.stock_qty ?? 0) <= 0}
            aria-label={`One fewer ${product.name}`}
          >
            −
          </button>
          <input
            className="product-stepper-input"
            type="number"
            min="0"
            step="1"
            value={draft}
            aria-label={`${product.name} in stock`}
            onFocus={() => setEditingQty(true)}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitQty}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setDraft(String(product.stock_qty ?? 0));
                setEditingQty(false);
                e.currentTarget.blur();
              }
            }}
          />
          <button
            type="button"
            onClick={() => onAdjustStock(product, 1)}
            aria-label={`One more ${product.name}`}
          >
            +
          </button>
        </div>
        <Badge tone={STOCK_TONE[state]}>{stockLabel(product)}</Badge>
      </div>

      <div className="product-row-actions">
        {needsRestock(product) &&
          (reorderStatus ? (
            // Already raised. Saying so beats offering a button that would
            // quietly put the same item on the list a second time.
            <span className="product-row-onlist">
              {reorderStatus === 'Ordered' ? 'On order' : 'On order list'}
            </span>
          ) : (
            <button type="button" className="ghost-button" onClick={() => onAddToOrder(product)}>
              Add to order list
            </button>
          ))}
        <button type="button" onClick={() => onEdit(product)}>
          Edit
        </button>
        <button type="button" onClick={() => onArchive(product)}>
          {product.is_active ? 'Archive' : 'Restore'}
        </button>
        {confirmDelete ? (
          <>
            <button
              type="button"
              className="danger"
              onClick={() => {
                setConfirmDelete(false);
                onDelete(product);
              }}
            >
              Delete for good
            </button>
            <button type="button" onClick={() => setConfirmDelete(false)}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className="danger" onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        )}
      </div>
    </li>
  );
}
